/**
 * Incidents Report Page
 *
 * Cross-event report listing practice incidents with event/rider/free-text
 * filtering and a detail drawer. Gated in App.tsx via `ProtectedRoute requireCoach`
 * (admits both coaches and admins).
 *
 * Behaviour overview:
 * - On mount, fires a single `restClient.getAllIncidents()` request — no
 *   pagination and no cache (Req 10.6 target ≤500 incidents).
 * - Client-side sort: event date descending (by the enriched `event_date`
 *   / `event_starttime` per Req 5.5); for ties, `timestamp` descending.
 *   Wrapped in try/catch — if any date parse yields NaN, the API order
 *   is preserved without throwing.
 * - Filter bar: event `<select>`, rider `<select>`, free-text `<input
 *   type="search">` (case-insensitive match against `incident_type`
 *   and `description`). All filter state is local; no refetch on change.
 *   A `useMemo` filter chain keeps updates snappy.
 * - Row fields: event date, event name, rider display name via
 *   `formatRiderName({ name: inc.rider_name ?? '', type: undefined })
 *   || 'Unknown Rider'`, `incident_type`, truncated `description`
 *   (200-char cutoff with inline "Show more" toggle), `recorded_by_display_name`
 *   labelled "Reported By" (fall back to raw `recorded_by` when display
 *   name is missing/empty — Req 16.7), and an optional row-level "View
 *   event incidents →" link.
 * - Partial-data: any of {event date, event name, rider display name,
 *   incident type, description, Reported By} missing → render row using
 *   available fields with a `"Not recorded"` placeholder for missing
 *   ones (Req 11.4).
 * - Detail drawer (shared `Modal`): full untruncated description rendered
 *   with `white-space: pre-wrap` (Req 11.2). When the caller is an admin,
 *   also shows `incident_id`. Returns focus to the triggering row on close.
 * - Broken-identifier row suppression: rows whose `recorded_by` is malformed
 *   (null, non-string, throws on render) AND whose `recorded_by_display_name`
 *   is missing/empty are filtered out before render; defense-in-depth via
 *   a per-row `ErrorBoundary` with `fallback={null}` catches anything that
 *   still throws during render (Req 15.3, Property 34).
 * - Empty-state copy:
 *     • `"No incidents have been reported yet."` when the API returns 0.
 *     • `"No incidents match these filters."` when filters reduce the
 *       list to 0.
 *
 * Mobile-first (Req 7.5): renders without horizontal scrolling between
 * 320px and 480px; filter bar stacks vertically on narrow viewports; row
 * fields wrap rather than overflow; every interactive control has
 * `min-h-[44px]`.
 *
 * Implements Requirements:
 *   9.1, 9.2, 9.3, 9.4, 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 10.7, 10.8,
 *   11.1, 11.2, 11.3, 11.4, 12.5, 14, 15.3, 16.5, 16.6, 16.7, 7.5.
 *
 * See: design.md §Components and Interfaces → Component hierarchy (detail)
 *      → IncidentsReport; §Error Handling and Edge Cases → item 3;
 *      §Correctness Properties → Property 34.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { restClient, Rider } from '../../api/rest-client';
import type { Incident } from '../../types/incidents';
import { useAuth } from '../../auth/AuthProvider';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { ErrorBoundary } from '../../components/common/ErrorBoundary';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import { useUIStore } from '../../store/uiStore';
import { formatRiderName } from '../../utils/riderDisplay';
import { IncidentCaptureDialog } from '../../components/features/incidents/IncidentCaptureDialog';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Inline-expand cutoff for the row description preview. */
const DESCRIPTION_PREVIEW_LIMIT = 200;

/** Placeholder shown for missing fields (Req 11.4). */
export const NOT_RECORDED = 'Not recorded';

// ---------------------------------------------------------------------------
// Safe field readers
// ---------------------------------------------------------------------------

/** Return a trimmed string when the value is a non-empty string, else null. */
export function asNonEmptyString(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Decide whether a row has a usable "Reported By" identity — either a
 * display name or a raw `recorded_by` identifier that is a non-empty
 * string (Req 15.3, 16.7, Property 34). One half of the broader
 * `isRenderableIncident` check below.
 */
export function rowHasUsableReporter(inc: Incident): boolean {
  const displayName = asNonEmptyString((inc as { recorded_by_display_name?: unknown }).recorded_by_display_name);
  if (displayName !== null) return true;
  const raw = asNonEmptyString((inc as { recorded_by?: unknown }).recorded_by);
  return raw !== null;
}

/**
 * Broader row-suppression predicate used as the list-view pre-filter
 * (Req 15.3, Property 34). A row is renderable iff every critical
 * identifier field is a non-empty, trimmable string AND the row has a
 * usable Reporter identity. Specifically:
 *
 *   - `incident_id`   — non-empty string (used as the React `key` and
 *                       for the admin detail-drawer code block)
 *   - `event_id`      — non-empty string (used by the event filter and
 *                       the per-row "View event incidents →" link)
 *   - `rider_id`      — non-empty string (used by the rider filter)
 *   - Reporter identity — `rowHasUsableReporter` returns true (i.e.
 *                       `recorded_by_display_name` is non-empty OR
 *                       the raw `recorded_by` identifier is non-empty).
 *
 * Rows that fail this check are suppressed from the list view — they
 * never reach the sort, filter, or row-render pipeline. The per-row
 * `ErrorBoundary` below is defense-in-depth for anything that still
 * throws at render time.
 */
export function isRenderableIncident(inc: Incident): boolean {
  if (asNonEmptyString((inc as { incident_id?: unknown }).incident_id) === null) {
    return false;
  }
  if (asNonEmptyString((inc as { event_id?: unknown }).event_id) === null) {
    return false;
  }
  if (asNonEmptyString((inc as { rider_id?: unknown }).rider_id) === null) {
    return false;
  }
  return rowHasUsableReporter(inc);
}

/** Pick the display name for "Reported By" with raw-identifier fallback. */
export function resolveReportedBy(inc: Incident): string | null {
  const displayName = asNonEmptyString((inc as { recorded_by_display_name?: unknown }).recorded_by_display_name);
  if (displayName !== null) return displayName;
  const raw = asNonEmptyString((inc as { recorded_by?: unknown }).recorded_by);
  return raw;
}

/** Rider display name with the spec's fallback (Unknown Rider). */
export function resolveRiderName(inc: Incident): string {
  const name = asNonEmptyString((inc as { rider_name?: unknown }).rider_name);
  return (
    formatRiderName({ name: name ?? '', type: undefined }) || 'Unknown Rider'
  );
}

/** Date string used for the primary sort (Req 5.5). */
function resolveEventDateRaw(inc: Incident): string | null {
  return asNonEmptyString((inc as { event_date?: unknown }).event_date);
}

/** Safely parse an ISO 8601 string to a timestamp (ms). Returns NaN on failure. */
function parseMillis(iso: string | null | undefined): number {
  if (!iso) return NaN;
  try {
    const d = new Date(iso);
    return d.getTime();
  } catch {
    return NaN;
  }
}

/**
 * Format the event date for list rows. Prefers a date-only display since
 * the enriched `event_date` is the event's start time. Falls back to the
 * raw ISO string on parse failure so a bad date never blanks the cell.
 */
function formatEventDate(iso: string | null | undefined): string {
  if (!iso) return NOT_RECORDED;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Sort + filter helpers
// ---------------------------------------------------------------------------

/**
 * Sort by event date desc, then `timestamp` desc for ties (Req 10.1,
 * Property 27). Wrapped in try/catch so a malformed date never throws.
 */
export function sortIncidents(incidents: Incident[]): Incident[] {
  try {
    return [...incidents].sort((a, b) => {
      const aEvent = parseMillis(resolveEventDateRaw(a));
      const bEvent = parseMillis(resolveEventDateRaw(b));
      // NaNs sort to the end so rows with bad dates do not jump above good ones.
      const aEventKey = Number.isNaN(aEvent) ? Number.NEGATIVE_INFINITY : aEvent;
      const bEventKey = Number.isNaN(bEvent) ? Number.NEGATIVE_INFINITY : bEvent;
      if (bEventKey !== aEventKey) return bEventKey - aEventKey;

      const aTs = parseMillis(a.timestamp);
      const bTs = parseMillis(b.timestamp);
      const aTsKey = Number.isNaN(aTs) ? Number.NEGATIVE_INFINITY : aTs;
      const bTsKey = Number.isNaN(bTs) ? Number.NEGATIVE_INFINITY : bTs;
      return bTsKey - aTsKey;
    });
  } catch {
    return incidents;
  }
}

export interface EventOption {
  event_id: string;
  event_name: string;
}

export interface RiderOption {
  rider_id: string;
  rider_name: string;
}

/** Distinct `(event_id, event_name)` pairs present in the list (Req 10.3). */
export function deriveEventOptions(incidents: Incident[]): EventOption[] {
  const seen = new Map<string, EventOption>();
  for (const inc of incidents) {
    const event_id = asNonEmptyString((inc as { event_id?: unknown }).event_id);
    if (!event_id) continue;
    if (seen.has(event_id)) continue;
    const event_name =
      asNonEmptyString((inc as { event_name?: unknown }).event_name) ?? event_id;
    seen.set(event_id, { event_id, event_name });
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.event_name.localeCompare(b.event_name),
  );
}

/** Distinct `(rider_id, rider_name)` pairs present in the list (Req 10.4). */
export function deriveRiderOptions(incidents: Incident[]): RiderOption[] {
  const seen = new Map<string, RiderOption>();
  for (const inc of incidents) {
    const rider_id = asNonEmptyString((inc as { rider_id?: unknown }).rider_id);
    if (!rider_id) continue;
    if (seen.has(rider_id)) continue;
    const rider_name =
      asNonEmptyString((inc as { rider_name?: unknown }).rider_name) ?? rider_id;
    seen.set(rider_id, { rider_id, rider_name });
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.rider_name.localeCompare(b.rider_name),
  );
}

/**
 * Apply the three filters (event, rider, free-text) in order. All filter
 * state is local; the incoming array is already sorted and row-suppressed.
 */
export function applyFilters(
  incidents: Incident[],
  eventFilter: string,
  riderFilter: string,
  searchText: string,
): Incident[] {
  const needle = searchText.trim().toLowerCase();
  return incidents.filter((inc) => {
    if (eventFilter) {
      const id = asNonEmptyString((inc as { event_id?: unknown }).event_id);
      if (id !== eventFilter) return false;
    }
    if (riderFilter) {
      const id = asNonEmptyString((inc as { rider_id?: unknown }).rider_id);
      if (id !== riderFilter) return false;
    }
    if (needle) {
      const type =
        typeof inc.incident_type === 'string' ? inc.incident_type : '';
      const description =
        typeof inc.description === 'string' ? inc.description : '';
      const hay = `${type}\n${description}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
}

/** User-facing message from a thrown load error. */
function extractLoadErrorMessage(err: unknown): string {
  const maybe = err as {
    response?: { data?: { error?: unknown; message?: unknown } };
    message?: unknown;
  };
  const serverError = maybe?.response?.data?.error;
  if (typeof serverError === 'string' && serverError.length > 0) {
    return serverError;
  }
  const serverMessage = maybe?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.length > 0) {
    return serverMessage;
  }
  if (!maybe?.response) {
    return 'Could not load incidents';
  }
  if (typeof maybe?.message === 'string' && maybe.message.length > 0) {
    return maybe.message;
  }
  return 'Could not load incidents';
}

// ---------------------------------------------------------------------------
// Row-scoped truncated description with inline "Show more"
// ---------------------------------------------------------------------------

function TruncatedDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const safe = typeof text === 'string' ? text : '';
  const needsTruncation = safe.length > DESCRIPTION_PREVIEW_LIMIT;
  if (!needsTruncation) {
    return (
      <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
        {safe}
      </p>
    );
  }
  return (
    <div className="text-sm text-gray-800">
      <p className="whitespace-pre-wrap break-words">
        {expanded ? safe : `${safe.slice(0, DESCRIPTION_PREVIEW_LIMIT)}…`}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1 text-primary-600 hover:text-primary-700 underline text-sm min-h-[44px]"
        aria-expanded={expanded}
      >
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Incident list row
// ---------------------------------------------------------------------------

interface IncidentRowProps {
  incident: Incident;
  onOpenDetail: (incident: Incident, trigger: HTMLElement | null) => void;
}

function IncidentRow({ incident, onOpenDetail }: IncidentRowProps) {
  const eventDateRaw = resolveEventDateRaw(incident);
  const eventDate = eventDateRaw ? formatEventDate(eventDateRaw) : NOT_RECORDED;
  const eventName =
    asNonEmptyString((incident as { event_name?: unknown }).event_name) ??
    NOT_RECORDED;
  const riderName = resolveRiderName(incident);
  const incidentType =
    asNonEmptyString((incident as { incident_type?: unknown }).incident_type) ??
    NOT_RECORDED;
  const description =
    asNonEmptyString((incident as { description?: unknown }).description) ??
    NOT_RECORDED;
  const reportedBy = resolveReportedBy(incident) ?? NOT_RECORDED;

  const eventId = asNonEmptyString(
    (incident as { event_id?: unknown }).event_id,
  );

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const trigger = e.currentTarget as HTMLElement;
      onOpenDetail(incident, trigger);
    },
    [incident, onOpenDetail],
  );

  // The card itself is a plain container — NOT an interactive element —
  // because it holds nested interactive children (the "Show more" toggle
  // inside TruncatedDescription and the "View event incidents →" Link).
  // Nesting interactive controls inside a `role="button"` violates axe's
  // `nested-interactive` (WCAG 2.1 AA). Instead, the primary "View
  // details" affordance is a dedicated `<button>` rendered inside the
  // card action row, keeping each interactive element top-level.
  return (
    <li>
      <div className="block w-full text-left bg-white rounded-lg shadow-md p-3 sm:p-6 transition-shadow">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wide text-gray-500">
                {eventDate}
              </p>
              <p className="text-base font-semibold text-gray-900 break-words">
                {eventName}
              </p>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 whitespace-nowrap">
              {incidentType}
            </span>
          </div>

          <p className="text-sm text-gray-700 break-words">
            <span className="font-medium">Rider:</span> {riderName}
          </p>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Description
            </p>
            {description === NOT_RECORDED ? (
              <p className="text-sm text-gray-500 italic">{NOT_RECORDED}</p>
            ) : (
              <TruncatedDescription text={description} />
            )}
          </div>

          <p className="text-xs text-gray-500 break-words">
            <span className="font-medium">Reported By:</span> {reportedBy}
          </p>

          <div className="pt-1 flex flex-wrap items-center gap-x-4 gap-y-2">
            <button
              type="button"
              onClick={handleOpen}
              aria-label={`View incident details for ${riderName} on ${eventDate}`}
              className="inline-flex items-center min-h-[44px] px-3 text-sm font-medium text-primary-700 hover:text-primary-800 underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
            >
              View details
            </button>
            {eventId && (
              <Link
                to={`/events/${eventId}/incidents`}
                className="inline-flex items-center min-h-[44px] text-sm text-primary-600 hover:text-primary-700 underline break-words"
              >
                View event incidents →
              </Link>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------

interface IncidentDetailDrawerProps {
  incident: Incident | null;
  isOpen: boolean;
  isAdmin: boolean;
  onClose: () => void;
  /** Admin-only. When provided and `isAdmin`, an Edit button appears in the drawer footer. */
  onEdit?: (incident: Incident) => void;
  /** Admin-only. When provided and `isAdmin`, a Delete button appears in the drawer footer. */
  onDelete?: (incident: Incident) => void;
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900 break-words">{children}</dd>
    </div>
  );
}

function IncidentDetailDrawer({
  incident,
  isOpen,
  isAdmin,
  onClose,
  onEdit,
  onDelete,
}: IncidentDetailDrawerProps) {
  if (!incident) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Incident Details">
        <p className="text-sm text-gray-600">No incident selected.</p>
      </Modal>
    );
  }

  const eventDateRaw = resolveEventDateRaw(incident);
  const eventDate = eventDateRaw ? formatEventDate(eventDateRaw) : NOT_RECORDED;
  const eventName =
    asNonEmptyString((incident as { event_name?: unknown }).event_name) ??
    NOT_RECORDED;
  const riderName = resolveRiderName(incident);
  const incidentType =
    asNonEmptyString((incident as { incident_type?: unknown }).incident_type) ??
    NOT_RECORDED;
  const description =
    asNonEmptyString((incident as { description?: unknown }).description) ??
    NOT_RECORDED;
  const reportedBy = resolveReportedBy(incident) ?? NOT_RECORDED;
  const incidentId = asNonEmptyString(
    (incident as { incident_id?: unknown }).incident_id,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Incident Details"
      className="sm:max-w-lg"
    >
      <dl className="space-y-4">
        <DetailRow label="Event Date">{eventDate}</DetailRow>
        <DetailRow label="Event">{eventName}</DetailRow>
        <DetailRow label="Rider">{riderName}</DetailRow>
        <DetailRow label="Incident Type">{incidentType}</DetailRow>
        <DetailRow label="Description">
          {description === NOT_RECORDED ? (
            <span className="text-gray-500 italic">{NOT_RECORDED}</span>
          ) : (
            // Preserve line breaks from the captured description (Req 11.2).
            <p className="whitespace-pre-wrap break-words">{description}</p>
          )}
        </DetailRow>
        <DetailRow label="Reported By">{reportedBy}</DetailRow>
        {isAdmin && (
          <DetailRow label="Incident ID">
            <code className="text-xs text-gray-700 break-all">
              {incidentId ?? NOT_RECORDED}
            </code>
          </DetailRow>
        )}
      </dl>
      <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          className="min-h-[44px] px-6"
        >
          Close
        </Button>
        {isAdmin && onEdit && (
          <Button
            type="button"
            variant="orange"
            onClick={() => onEdit(incident)}
            className="min-h-[44px] px-6"
            aria-label="Edit this incident"
          >
            Edit
          </Button>
        )}
        {isAdmin && onDelete && (
          <Button
            type="button"
            variant="danger"
            onClick={() => onDelete(incident)}
            className="min-h-[44px] px-6"
            aria-label="Delete this incident"
          >
            Delete
          </Button>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function IncidentsReport() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const showToast = useUIStore((state) => state.showToast);

  // Data state (Req 10.1, 10.6 — single fetch, no pagination, no cache).
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Filter state — local only; never refetches (Req 10.6).
  const [eventFilter, setEventFilter] = useState<string>('');
  const [riderFilter, setRiderFilter] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');

  // Detail drawer state + ref to the triggering row for focus return.
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(
    null,
  );
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Admin-only edit/delete state (post-launch enhancement). The drawer
  // opens Edit or Delete; the dialog/confirm-modal completes the action
  // and we refresh the report list in place on success.
  const [editingIncident, setEditingIncident] = useState<Incident | null>(null);
  const [deletingIncident, setDeletingIncident] = useState<Incident | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Roster for the edit dialog's rider selector. Fetched lazily the first
  // time an admin opens Edit so coaches visiting the page never trigger
  // an extra request. Cached for the lifetime of the page.
  const [editRoster, setEditRoster] = useState<Rider[]>([]);
  const [editRosterLoading, setEditRosterLoading] = useState<boolean>(false);
  const editRosterLoadedRef = useRef<boolean>(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const fresh = await restClient.getAllIncidents();
      setIncidents(Array.isArray(fresh) ? fresh : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[IncidentsReport] Failed to load incidents', err);
      setLoadError(extractLoadErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Row-suppression pre-filter (Req 15.3, Property 34). A row is dropped
  // when its critical identifiers (incident_id / event_id / rider_id)
  // are missing or malformed, OR when both `recorded_by_display_name`
  // and raw `recorded_by` are missing/empty. Per-row ErrorBoundary
  // below catches anything that still throws at render time.
  const renderableIncidents = useMemo(
    () => incidents.filter(isRenderableIncident),
    [incidents],
  );

  const sortedIncidents = useMemo(
    () => sortIncidents(renderableIncidents),
    [renderableIncidents],
  );

  const eventOptions = useMemo(
    () => deriveEventOptions(sortedIncidents),
    [sortedIncidents],
  );
  const riderOptions = useMemo(
    () => deriveRiderOptions(sortedIncidents),
    [sortedIncidents],
  );

  const filteredIncidents = useMemo(
    () => applyFilters(sortedIncidents, eventFilter, riderFilter, searchText),
    [sortedIncidents, eventFilter, riderFilter, searchText],
  );

  const handleOpenDetail = useCallback(
    (incident: Incident, trigger: HTMLElement | null) => {
      triggerRef.current = trigger;
      setSelectedIncident(incident);
      setDrawerOpen(true);
    },
    [],
  );

  const handleCloseDetail = useCallback(() => {
    setDrawerOpen(false);
    // Defer so the Modal has time to unmount its focus handling.
    setTimeout(() => {
      try {
        triggerRef.current?.focus();
      } catch {
        /* no-op — focus return is best-effort */
      }
    }, 0);
  }, []);

  /**
   * Lazy-load the roster used by the edit dialog's rider selector.
   * Called the first time an admin clicks Edit. Subsequent opens reuse
   * the cached list so we don't re-hit /riders on every edit click.
   */
  const ensureEditRoster = useCallback(async () => {
    if (editRosterLoadedRef.current) return;
    editRosterLoadedRef.current = true;
    setEditRosterLoading(true);
    try {
      const currentSeason = new Date().getFullYear().toString();
      const fresh = await restClient.getRiders(currentSeason);
      const active = (Array.isArray(fresh) ? fresh : []).filter(
        (r) => r.disabled !== '1' && r.disabled !== 1,
      );
      setEditRoster(active);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[IncidentsReport] Failed to load roster for edit dialog', err);
      // Allow retry on the next open.
      editRosterLoadedRef.current = false;
      showToast('error', 'Could not load rider roster for edit');
    } finally {
      setEditRosterLoading(false);
    }
  }, [showToast]);

  /** Admin-only: open the capture dialog in edit mode for the detail drawer's incident. */
  const handleOpenEditFromDrawer = useCallback(
    (incident: Incident) => {
      // Close the detail drawer first so we don't have two modals
      // stacked on top of each other (the Modal's Esc + focus trap
      // behaviour gets confused otherwise).
      setDrawerOpen(false);
      setEditingIncident(incident);
      void ensureEditRoster();
    },
    [ensureEditRoster],
  );

  const handleCloseEdit = useCallback(() => {
    setEditingIncident(null);
  }, []);

  /** Admin-only: open the delete confirm modal for the detail drawer's incident. */
  const handleOpenDeleteFromDrawer = useCallback((incident: Incident) => {
    setDrawerOpen(false);
    setDeletingIncident(incident);
    setDeleteError(null);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setDeletingIncident(null);
    setDeleteError(null);
    setDeleting(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingIncident) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await restClient.deleteIncident(deletingIncident.incident_id);
      showToast('success', 'Incident deleted');
      // Refresh the report list in place. Keeping the delete flow
      // synchronous with the refresh here avoids a brief "deleted but
      // still visible" flicker.
      try {
        const fresh = await restClient.getAllIncidents();
        setIncidents(Array.isArray(fresh) ? fresh : []);
      } catch (refreshErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[IncidentsReport] Failed to refresh incidents after delete',
          refreshErr,
        );
      }
      handleCloseDelete();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[IncidentsReport] Failed to delete incident', err);
      setDeleteError(extractLoadErrorMessage(err));
      setDeleting(false);
    }
  }, [deletingIncident, showToast, handleCloseDelete]);

  /** After a successful edit, refresh the report list in place. */
  const handleEditSuccess = useCallback(async () => {
    try {
      const fresh = await restClient.getAllIncidents();
      setIncidents(Array.isArray(fresh) ? fresh : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[IncidentsReport] Failed to refresh incidents after edit', err);
    }
  }, []);

  const hasIncidents = sortedIncidents.length > 0;
  const hasFilteredIncidents = filteredIncidents.length > 0;
  const filtersActive = Boolean(eventFilter || riderFilter || searchText.trim());

  return (
    <div className="max-w-5xl mx-auto">
      {/* Top action row — Back */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/admin/reports')}
          className="min-h-[44px] px-4"
          aria-label="Back to reports"
        >
          ← Back to Reports
        </Button>
      </div>

      {/* Page header */}
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-primary-900">Incidents Report</h1>
        <p className="text-sm text-gray-600 mt-1">
          Practice incidents reported across all events.
        </p>
      </header>

      {/* Filter bar — stacks vertically on narrow viewports (Req 7.5). */}
      <section
        aria-label="Filter incidents"
        className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-2"
      >
        <div>
          <label
            htmlFor="incidents-event-filter"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            Event
          </label>
          <select
            id="incidents-event-filter"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All events</option>
            {eventOptions.map((opt) => (
              <option key={opt.event_id} value={opt.event_id}>
                {opt.event_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="incidents-rider-filter"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            Rider
          </label>
          <select
            id="incidents-rider-filter"
            value={riderFilter}
            onChange={(e) => setRiderFilter(e.target.value)}
            className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All riders</option>
            {riderOptions.map((opt) => (
              <option key={opt.rider_id} value={opt.rider_id}>
                {opt.rider_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="incidents-search"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            Search
          </label>
          <input
            id="incidents-search"
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search type or description…"
            className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </section>

      {/* Summary / counts */}
      {!loading && !loadError && hasIncidents && (
        <p className="text-sm text-gray-600 mb-3">
          Showing{' '}
          <span className="font-semibold">{filteredIncidents.length}</span> of{' '}
          <span className="font-semibold">{sortedIncidents.length}</span>{' '}
          incident{sortedIncidents.length === 1 ? '' : 's'}
          {filtersActive ? ' (filtered)' : ''}.
        </p>
      )}

      {/* Loading / Error / Empty / List */}
      {loading && (
        <div
          className="flex items-center justify-center py-12"
          role="status"
          aria-live="polite"
        >
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Loading incidents…</span>
        </div>
      )}

      {!loading && loadError && (
        <Card className="border border-red-300 bg-red-50">
          <div role="alert" aria-live="assertive" className="text-sm text-red-800">
            <p className="font-semibold">{loadError}</p>
          </div>
          <div className="mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={loadData}
              className="min-h-[44px] px-4"
            >
              Retry
            </Button>
          </div>
        </Card>
      )}

      {!loading && !loadError && !hasIncidents && (
        <Card>
          <p className="text-gray-700">No incidents have been reported yet.</p>
        </Card>
      )}

      {!loading && !loadError && hasIncidents && !hasFilteredIncidents && (
        <Card>
          <p className="text-gray-700">No incidents match these filters.</p>
        </Card>
      )}

      {!loading && !loadError && hasFilteredIncidents && (
        <ul className="space-y-3" aria-label="Incidents list">
          {filteredIncidents.map((inc) => (
            // Per-row ErrorBoundary — defense-in-depth for any row that
            // throws during render (Req 15.3, Property 34).
            <ErrorBoundary key={inc.incident_id} fallback={null}>
              <IncidentRow incident={inc} onOpenDetail={handleOpenDetail} />
            </ErrorBoundary>
          ))}
        </ul>
      )}

      <IncidentDetailDrawer
        incident={selectedIncident}
        isOpen={drawerOpen}
        isAdmin={isAdmin}
        onClose={handleCloseDetail}
        onEdit={isAdmin ? handleOpenEditFromDrawer : undefined}
        onDelete={isAdmin ? handleOpenDeleteFromDrawer : undefined}
      />

      {/* Admin-only edit dialog. Reuses IncidentCaptureDialog in mode='edit'. */}
      {editingIncident && (
        <IncidentCaptureDialog
          eventId={editingIncident.event_id}
          eventName={editingIncident.event_name ?? ''}
          eventStartTime={editingIncident.event_date ?? ''}
          riders={editRoster}
          isOpen={Boolean(editingIncident) && !editRosterLoading}
          onClose={handleCloseEdit}
          onSuccess={handleEditSuccess}
          mode="edit"
          initialValues={{
            incident_id: editingIncident.incident_id,
            rider_id: editingIncident.rider_id,
            incident_type: editingIncident.incident_type,
            description: editingIncident.description,
          }}
        />
      )}

      {/* Admin-only delete confirm modal. */}
      {deletingIncident && (
        <Modal
          isOpen={Boolean(deletingIncident)}
          onClose={deleting ? () => {} : handleCloseDelete}
          title="Delete Incident"
          className="sm:max-w-md"
          showCloseButton={!deleting}
        >
          <p className="text-sm text-gray-800">
            Delete this incident? This cannot be undone.
          </p>
          {deleteError && (
            <div
              role="alert"
              aria-live="assertive"
              className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
            >
              {deleteError}
            </div>
          )}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCloseDelete}
              disabled={deleting}
              className="min-h-[44px] px-6"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="min-h-[44px] px-6"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
